/* eslint-disable react/prop-types */
import { Box, Button, Spinner } from '@radix-ui/themes';
import axios from 'axios';
import ApiKey from 'Components/api/ApiKey';
import BaseApi from 'Components/api/BaseApi';
import { JobCategory } from 'jobs/types';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// helpers
import { formatPhoneNumber, resultInfo } from '../../Components/helpers';
import { SimpleCv, SimpleCvGetcontactResponse } from './types';

const workTypeSettings = [
   { name: 1, text: 'searchWorkTypeFullTime' },
   { name: 2, text: 'searchWorkTypePartTime' },
   { name: 3, text: 'searchWorkTypeRemoteWork' },
   { name: 4, text: 'searchWorkTypeVariableSchedule' },
   { name: 5, text: 'searchWorkTypeFlexibleSchedule' },
   { name: 6, text: 'searchWorkTypeWatchMethod' },
];

const Questionnaire = ({
   cvDetails,
   isOpenContact,
}: {
   isOpenContact: boolean;
   cvDetails: {
      jobInfo: any;
      jobApply: any;
      simpleCv: SimpleCv;
      categories: Array<JobCategory>;
      userdetail: any;
   };
}) => {
   const { t } = useTranslation('global');

   const userType = Cookies.get('user_type');
   const tokenKey = Cookies.get('tokenClient');
   const apikey = ApiKey;

   const { simpleCv, categories = [], userdetail = {} } = cvDetails;

   const { contactsData, contactsLoading, getContacts } = useSimpleCvContacts({
      token: tokenKey ?? '',
      apikey: apikey,
      cvId: simpleCv.id,
      isOpenContact,
   });

   const categoriesEl = [simpleCv?.category_id, simpleCv?.category_id_1, simpleCv?.category_id_2]
      .filter((cat) => !!cat)
      .map((catId) => {
         const category = categories.find((c) => c.id === catId);
         if (category) {
            return category.name;
         }
      })
      .join('; ');

   const specializations = [simpleCv.subcategory_id].reduce<Array<string>>((acc, subId) => {
      const subCat = simpleCv.subcategories.find((sub) => sub.id.toString() === subId?.toString());
      if (subCat) {
         return [...acc, subCat.name];
      }
      return acc;
   }, []);

   const renderEmail = () => {
      if (!contactsData?.email_address) {
         return null;
      }
      if (contactsData.email_address.includes('@email.grc.ua')) {
         return null;
      }
      return (
         <p>
            <i className="fa-solid fa-envelope" />
            <a href={`mailto:${contactsData?.email_address}`}>{contactsData?.email_address}</a>
         </p>
      );
   };

   return (
      <div className="cvDescription">
         <div className="personalInfoSection">
            <div>
               <h1>
                  {simpleCv.position}
                  <span className="ms-2 fs-4 text-muted">
                     ({t('employerInnerAccountdetail.questionnaire')})
                  </span>
               </h1>

               {!contactsData && !isOpenContact && (
                  <Box mb="4">
                     <Button
                        color="red"
                        size="4"
                        radius="small"
                        loading={contactsLoading}
                        onClick={() => {
                           getContacts();
                        }}
                     >
                        Відкрити контакти
                     </Button>
                  </Box>
               )}

               {!contactsData && isOpenContact && contactsLoading ? (
                  <Box p="4" pb="6">
                     <Spinner size="3" />
                  </Box>
               ) : (
                  <>
                     <div className="personalInfoBox">
                        <h2>
                           {contactsData?.last_name} {contactsData?.first_name}
                        </h2>
                     </div>
                     <div className="contactsBox">
                        {contactsData?.contact_number && (
                           <p>
                              <i className="fa-solid fa-phone" />
                              <a href={`tel:${contactsData?.contact_number}`}>
                                 {formatPhoneNumber(contactsData?.contact_number)}
                              </a>
                           </p>
                        )}
                        {renderEmail()}
                     </div>
                  </>
               )}

               <div className="wishesInfoBox">
                  <p>
                     <i className="fa-solid fa-sitemap" />
                     <span>
                        {t('cvDescription.category')}
                        {': '}
                     </span>
                     {categoriesEl}
                  </p>
                  <p>
                     <i className="fa-solid fa-sitemap" />
                     <span>
                        {t('cvDescription.subCategory')}
                        {': '}
                     </span>
                     {specializations}
                  </p>
                  <p>
                     <i className="fa-sharp fa-solid fa-location-dot" />
                     <span>
                        {t('cvDescription.relocations')}
                        {': '}
                     </span>
                     {simpleCv?.city.city_name && simpleCv.city.city_name}
                     {simpleCv?.city.country_name && `, ${simpleCv.city.country_name}`}
                  </p>
                  <p>
                     <i className="fa-solid fa-clock" />
                     <span>
                        {t('cvDescription.workType')}
                        {': '}
                     </span>
                     {t(
                        `jobseekerListing.${workTypeSettings.find((i) => i.name.toString() === simpleCv.work_type.toString())?.text}`
                     )}
                  </p>
               </div>
            </div>
            <div className="profileImageBox">
               <img
                  alt="profile image"
                  className="profileImage"
                  src="/Images/jobseekerSide/dummy-profile.png"
               />
            </div>
         </div>
      </div>
   );
};

export default Questionnaire;

function useSimpleCvContacts({
   token,
   apikey,
   cvId,
   onError,
   onSuccess,
   isOpenContact,
}: {
   token: string;
   apikey: string;
   cvId: string | number;
   onError?: () => void;
   onSuccess?: () => void;
   isOpenContact: boolean;
}) {
   const [contactsData, setContactsData] = useState<SimpleCvGetcontactResponse['response'] | null>(
      null
   );
   const [smallLoading, setSmallLoading] = useState(false);
   const { t } = useTranslation('global');

   useEffect(() => {
      console.log(isOpenContact);
      if (isOpenContact) {
         getContacts();
      }
   }, [isOpenContact]);

   const getContacts = async () => {
      try {
         setSmallLoading(true);

         const response = await axios.post<SimpleCvGetcontactResponse>(
            BaseApi + `/candidates/getSimplePageContacts/${cvId}`,
            null,
            {
               headers: {
                  key: apikey,
                  token: token,
                  'Content-Type': 'application/json',
               },
            }
         );

         setSmallLoading(false);

         if (response.data.status === 200) {
            setContactsData(response.data.response);
            onSuccess?.();
         } else if (response.data.status === 422) {
            resultInfo('warning', response.data.message, t('jobseekerManageAlert.close'));
         } else {
            resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
         }
      } catch (error: any) {
         setSmallLoading(false);

         if (error.response.data.status === 422) {
            resultInfo('warning', error.response.data.message, t('jobseekerManageAlert.close'));
         } else if (error.response.status === 500) {
            resultInfo('error', error.message, t('jobseekerManageAlert.close'));
         }

         console.error('Could not get contacts data', error);
      }
   };

   return { contactsData, contactsLoading: smallLoading, getContacts };
}
